import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, User, Menu, X, LogOut, CalendarCheck, Shield } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useClientAuth } from "../context/ClientAuthContext";

const NAV_LINKS = [
  { to: "/", label: "À Propos" },
  { to: "/tarifs", label: "Nos Tarifs" },
  { to: "/le-coach", label: "Le Coach" },
  { to: "/blog", label: "Blog" },
  { to: "/credit-impot", label: "Crédit d'impôts", short: "Crédit d'impôts" },
  { to: "/contact", label: "Contact" },
];

// Client accounts who get a shortcut to the admin dashboard.
// (Owner of FORM'ELEC.)
const ADMIN_EMAILS = ["rosalie@form-elec.fr", "rosariveti@gmail.com"];

function getInitials(prenom = "", nom = "") {
  return ((prenom?.[0] || "") + (nom?.[0] || "")).toUpperCase() || "U";
}

function AvatarButton({ client, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-2.5 pl-1 pr-4 py-1 rounded-full bg-fegray-dark text-white hover:bg-fegreen transition-all group"
    >
      <span className="w-8 h-8 rounded-full bg-fegreen text-white font-bold text-sm flex items-center justify-center group-hover:bg-white group-hover:text-fegreen transition-colors">
        {getInitials(client.prenom, client.nom)}
      </span>
      <span className="text-sm font-semibold pr-1">{client.prenom}</span>
    </button>
  );
}

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const { user: adminUser } = useAuth();
  const { client, ready, logout: clientLogout } = useClientAuth();
  const navigate = useNavigate();
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  React.useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleLogoutClient = async () => {
    await clientLogout();
    setMenuOpen(false);
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      {/* Top bar */}
      <div className="topbar-gradient">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-2 flex flex-wrap items-center justify-center md:justify-between gap-x-5 gap-y-1 text-fegray-dark text-xs sm:text-sm font-medium">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <a href="tel:+33766253143" className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-phone">
              <Phone className="w-3.5 h-3.5 text-white" /> 07 66 25 31 43
            </a>
            <a href="mailto:rosariveti@gmail.com" className="hidden sm:flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-email">
              <Mail className="w-3.5 h-3.5 text-white" /> rosariveti@gmail.com
            </a>
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-white" /> Istres &amp; Alentours
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden lg:inline text-fegray-dark/80 hover:text-white transition-colors">Suivez-nous sur</span>
            <a href="https://www.facebook.com/people/Formelec/100086893831574/" target="_blank" rel="noreferrer" aria-label="Facebook FORM'ELEC" data-testid="header-fb" className="text-black hover:opacity-70 transition-opacity"><Facebook className="w-4 h-4 fill-black" /></a>
            <a href="https://www.instagram.com/formelec_istres/" target="_blank" rel="noreferrer" aria-label="Instagram FORM'ELEC" data-testid="header-ig" className="text-black hover:opacity-70 transition-opacity"><Instagram className="w-4 h-4" /></a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 lg:py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex-shrink-0">
            <Logo size="sm" className="lg:hidden" />
            <Logo size="md" className="hidden lg:block" />
          </Link>

          <nav className="hidden xl:flex items-center gap-7 flex-1 justify-center">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-semibold whitespace-nowrap transition-colors ${
                    isActive ? "text-fegreen" : "text-fegray hover:text-fegreen"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <nav className="hidden lg:flex xl:hidden items-center gap-5 flex-1 justify-center">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-semibold whitespace-nowrap transition-colors ${
                    isActive ? "text-fegreen" : "text-fegray hover:text-fegreen"
                  }`
                }
              >
                {l.short || l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            {/* Reserve button */}
            <Link to="/reservation" className="btn-green !py-2.5 !px-5 text-sm" data-testid="header-reserve-btn">
              <CalendarCheck className="w-4 h-4" /> Réserver
            </Link>

            {/* Admin shortcut */}
            {adminUser && adminUser.role === "admin" && (
              <button onClick={() => navigate("/admin")} className="text-xs font-semibold text-fegray hover:text-fegreen transition-colors" data-testid="header-admin-link">
                Admin
              </button>
            )}

            {/* Client account */}
            {ready && client ? (
              <div className="relative" ref={dropdownRef}>
                <AvatarButton client={client} onClick={() => setMenuOpen(!menuOpen)} testid="header-client-avatar" />
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="text-sm font-bold text-fegray-dark">{client.prenom} {client.nom}</div>
                      <div className="text-xs text-fegray truncate">{client.email}</div>
                    </div>
                    <Link to="/account" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-fegray-dark hover:bg-fegreen-light hover:text-fegreen transition-colors" data-testid="account-link">
                      <User className="w-4 h-4" /> Mon compte
                    </Link>
                    <Link to="/account/reservations" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-sm text-fegray-dark hover:bg-fegreen-light hover:text-fegreen transition-colors">
                      <CalendarCheck className="w-4 h-4" /> Mes séances
                    </Link>
                    {ADMIN_EMAILS.includes((client.email || "").toLowerCase()) && (
                      <Link
                        to="/admin"
                        onClick={() => setMenuOpen(false)}
                        data-testid="account-admin-shortcut"
                        className="flex items-center gap-2 px-4 py-3 text-sm font-bold bg-fegray-dark text-white hover:bg-fegreen transition-colors border-t border-gray-100"
                      >
                        <Shield className="w-4 h-4" /> Espace administrateur
                      </Link>
                    )}
                    <button onClick={handleLogoutClient} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors border-t border-gray-100" data-testid="client-logout">
                      <LogOut className="w-4 h-4" /> Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/account/login"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-fegray-dark text-white hover:bg-fegreen transition-all text-sm font-semibold"
                data-testid="header-login-btn"
              >
                <User className="w-4 h-4" /> Mon compte
              </Link>
            )}
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-fegray-dark p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            aria-expanded={open}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 top-[88px] bg-white z-40 overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-base font-semibold py-3 border-b border-gray-100 ${
                    isActive ? "text-fegreen" : "text-fegray-dark"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/reservation" onClick={() => setOpen(false)} className="btn-green mt-6 w-full">
              <CalendarCheck className="w-4 h-4" /> Réserver
            </Link>
            {ready && client ? (
              <>
                <Link to="/account" onClick={() => setOpen(false)} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-fegray-dark text-white">
                  <span className="w-7 h-7 rounded-full bg-fegreen text-white font-bold text-xs flex items-center justify-center">
                    {getInitials(client.prenom, client.nom)}
                  </span>
                  {client.prenom}
                </Link>
                <button onClick={async () => { await clientLogout(); setOpen(false); navigate("/"); }} className="mt-2 text-sm text-red-500 py-2">
                  Déconnexion
                </button>
              </>
            ) : (
              <Link to="/account/login" onClick={() => setOpen(false)} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-fegray-dark text-white font-semibold">
                <User className="w-4 h-4" /> Mon compte
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
